// Распознавание формата вставленной/введённой формулы и перевод в LaTeX:
// LaTeX (в т. ч. $…$, \[…\], \begin{equation}), линейная запись Word (UnicodeMath), AsciiMath, MathML, OMML,
// HTML из Word с формулами, химическая запись, текст с символами Юникода.
import { canon, chemToLatex, looksChemical } from './latex';
import { convertLinear, looksLinear } from './unicodemath';
import { mathmlToLatex, ommlToLatex, convertWordEquations } from './omml';

export type MathFormat = 'latex' | 'linear' | 'asciimath' | 'mathml' | 'omml' | 'chem' | 'text';
export const FORMAT_NAMES: Record<MathFormat, string> = {
  latex: 'LaTeX', linear: 'линейная запись Word', asciimath: 'AsciiMath', mathml: 'MathML', omml: 'формула Word (OMML)',
  chem: 'химическая запись', text: 'текст',
};

export interface Detected { latex: string; format: MathFormat; display?: boolean; numbered?: boolean }

/** Внешний преобразователь AsciiMath (MathLive), подключается из ml.ts - модуль остаётся без зависимостей. */
let asciiConv: ((s: string) => string) | null = null;
export function setAsciiMathConverter(fn: (s: string) => string) { asciiConv = fn; }

const ASCII_WORDS = /\b(?:sqrt|root|sum_|prod_|int_|oint|lim_|oo|frac|vec|hat|bar|ubrace|obrace|dot|ddot|abs|floor|ceil|norm|del|grad|quad|cdots|xx|\*\*|-:|in|notin|sub|sup|sube|supe|uu|nn|AA|EE|RR|NN|ZZ|QQ|CC|alpha|beta|gamma|delta|epsilon|theta|lambda|mu|pi|rho|sigma|tau|phi|omega|Delta|Omega|Sigma|Phi)\b/;

/** Определить формат строки. */
export function detectFormat(src: string): MathFormat {
  const s = (src || '').trim();
  if (!s) return 'text';
  if (/^<(?:\?xml|!--|html|body|p|span|div)?[\s\S]*(msEquation|<m:oMath)/i.test(s) || /^<m:oMath/i.test(s)) return 'omml';
  if (/<(mml:)?math[\s>]/i.test(s)) return 'mathml';
  if (/^\$\$?[\s\S]+\$\$?$/.test(s) || /^\\\[|^\\\(|\\begin\{|\\(frac|sqrt|sum|int|alpha|beta|gamma|delta|cdot|times|left|right|mathrm|text|vec|bar|lim|infty|le|ge|ne|approx|pi|sigma|omega|rho|lambda|mu|Delta|partial)\b|[\^_]\{/.test(s)) return 'latex';
  if (/\\ce\{/.test(s)) return 'latex';
  if (looksChemical(s)) return 'chem';
  if (/[▒■█┬┴〖〗⁡]|[\u{1D400}-\u{1D7FF}]/u.test(s)) return 'linear';
  if (ASCII_WORDS.test(s) && !/[А-Яа-яЁё]{3,}/.test(s)) return 'asciimath';
  if (looksLinear(s)) return 'linear';
  return 'text';
}

/** Любой поддерживаемый формат -> LaTeX (приведённый к СТО). format - принудительно. */
export function anyToLatex(src: string, format?: MathFormat): Detected {
  const s = (src || '').trim();
  const f = format || detectFormat(s);
  let latex = '';
  let display: boolean | undefined;
  let numbered: boolean | undefined;
  switch (f) {
    case 'omml': {
      if (/msEquation/i.test(s)) {
        const m = s.match(/<!--\[if gte msEquation 12\]>([\s\S]*?)<!\[endif\]-->/i);
        const r = ommlToLatex(m ? m[1] : s);
        latex = r.latex; display = r.display; numbered = r.numbered || undefined;
      } else {
        const r = ommlToLatex(s);
        latex = r.latex; display = r.display; numbered = r.numbered || undefined;
      }
      break;
    }
    case 'mathml': { const r = mathmlToLatex(s); latex = r.latex; display = r.display; break; }
    case 'latex': {
      display = /^\$\$|^\\\[|\\begin\{(equation|align|gather|multline)/.test(s);
      numbered = /\\begin\{(equation|align|gather)\}/.test(s) || undefined;
      latex = s;
      break;
    }
    case 'chem': latex = chemToLatex(s); break;
    case 'asciimath': latex = asciiConv ? asciiConv(s) : convertLinear(s); break;
    case 'linear': case 'text': latex = convertLinear(s); break;
  }
  return { latex: canon(latex).latex, format: f, display, numbered };
}

export { convertWordEquations };
